/**
 * SuperadminNotificationContext
 *
 * Provides real-time audit log notifications for all authenticated users.
 * The Provider component lives in ./SuperadminNotificationProvider — this file
 * holds the context object + hooks (react-refresh/only-export-components).
 */

import { createContext, useContext } from 'react';
import type { AuditLog } from '../types';

export interface NotificationContextValue {
  // Recent notifications for the bell popover
  recentNotifications: AuditLog[];
  // Unread count for the badge
  unreadCount: number;
  // Mark all as read
  markAllAsRead: () => void;
  // Clear all notifications
  clearNotifications: () => void;
  // Whether notifications are enabled
  isEnabled: boolean;
  // Toggle notifications
  setIsEnabled: (enabled: boolean) => void;
  // Whether currently polling
  isPolling: boolean;
  // Last poll timestamp
  lastPollTime: Date | null;
}

export const SuperadminNotificationContext = createContext<NotificationContextValue | null>(null);

export function useSuperadminNotifications() {
  const context = useContext(SuperadminNotificationContext);
  if (!context) {
    throw new Error('useSuperadminNotifications must be used within SuperadminNotificationProvider');
  }
  return context;
}

// Safe hook that returns null if not in provider (for components that may be outside provider)
export function useSuperadminNotificationsSafe(): NotificationContextValue | null {
  return useContext(SuperadminNotificationContext);
}
